import { graphql } from 'react-relay';
import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useIsAllowedToCreateWhiteboard } from '@confluence/content-types-utils/entry-points/useIsAllowedToCreateWhiteboard.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';

import type { usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery as usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery$data } from './__types__/usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery.ts';
import type { usePreloadCreationMenuDataAgentPermissionsQuery$data } from './__generated__/usePreloadCreationMenuDataAgentPermissionsQuery.graphql.ts';

const usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery = graphql`
	query usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery {
		paywallContentToDisable(contentType: "whiteboard") {
			deactivationIdentifier
			link
			title
		}
	}
`;

const RovoAgentPermissionsQuery = graphql`
	query usePreloadCreationMenuDataAgentPermissionsQuery($cloudId: ID!) {
		atlassianStudio_userSiteContext(cloudId: $cloudId) {
			... on AtlassianStudioUserSiteContextOutput {
				userPermissions {
					isAbleToCreateAgents
				}
			}
		}
	}
`;

type UsePreloadCreationMenuDataResponse = () => void;
export const usePreloadCreationMenuData = (): UsePreloadCreationMenuDataResponse => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { edition, cloudId, isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const shouldPreloadAgentData =
		isRovoLLMEnabled &&
		!isAnonymousUser &&
		cloudId &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('cc_fd_agent_creation_connie_create_gate');

	const isAllowedToCreateWhiteboard = useIsAllowedToCreateWhiteboard();
	const [preloadWhiteboardPaywallData] =
		useLazyQueryInterop<usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery$data>(
			usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery,
		);
	const [preloadAgentPermissionsData] =
		useLazyQueryInterop<usePreloadCreationMenuDataAgentPermissionsQuery$data>(
			RovoAgentPermissionsQuery,
			{
				interopApolloAggClient: true,
				onCompleted: (data) => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'createContentPopover',
							action: 'success',
							actionSubject: 'studioPermissionsQuery',
							actionSubjectId: 'createMenuPreloader',
							attributes: {
								isAbleToCreateAgents:
									data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents,
							},
						},
					}).fire();
				},
				onError: (error) => {
					if (
						!isErrorMarkedAsHandled(error) &&
						(isUnauthorizedError(error) || error?.message.includes('Failed to fetch'))
					) {
						markErrorAsHandled(error);
						return;
					}

					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'createContentPopover',
							action: 'error',
							actionSubject: 'studioPermissionsQuery',
							actionSubjectId: 'createMenuPreloader',
							attributes: {
								errorMessage: error.message,
							},
						},
					}).fire();
				},
			},
		);
	const shouldPreloadPaywallData = isAllowedToCreateWhiteboard && !isPremium;
	return useCallback(() => {
		if (shouldPreloadPaywallData) {
			preloadWhiteboardPaywallData();
		}
		if (shouldPreloadAgentData) {
			preloadAgentPermissionsData({ variables: { cloudId } });
		}
	}, [
		preloadWhiteboardPaywallData,
		shouldPreloadPaywallData,
		preloadAgentPermissionsData,
		shouldPreloadAgentData,
		cloudId,
	]);
};
